import { LuBolt, LuTriangleAlert } from 'react-icons/lu'

import { GEO_FEATURE_MARKER_COLOR, HIGH_RISK_MARKER_COLOR } from '@/feature/observatory'

import { MarkerBadge } from '@/components/MarkerBadge'

import { ReplayTimeRange } from '../ReplayTimeRange'
import styles from './ReplayTimeline.module.css'
import { ReplayTimelineProps } from './ReplayTimeline.types'
import { SPEED_LEGEND } from './ReplayTimeline.utils'
import { useReplayTimeline } from './useReplayTimeline'

/**
 * The replay bar: a legend, a sampling-granularity slider, and the scrubbable timeline of the
 * asset's movement and geofence events.
 *
 * Takes its range and selection as props rather than reading a page store, so it can serve both
 * the observatory (whose selection persists into the URL) and the execution board's task focus
 * modal (which keeps replay state local to the modal).
 */
export const ReplayTimeline = ({
  asset,
  rangeStartTime,
  rangeEndTime,
  selectionStartTime,
  selectionEndTime,
  setSelectionStartTime,
  setSelectionEndTime,
  stepSize,
  setStepSize,
  history,
}: ReplayTimelineProps) => {
  const { markerTracks, stepValue, setStepValue } = useReplayTimeline({
    asset,
    rangeStartTime,
    stepSize,
    setStepSize,
    history,
  })

  return (
    <div className={styles.replayTimeRange} data-testid="replay-timeline">
      <div className={styles.replayTimeRangeHeader}>
        <div className={styles.geoFenceLegend}>
          <div className={styles.replayTimeRangeHeaderText}>
            <MarkerBadge glyph={LuBolt} color={GEO_FEATURE_MARKER_COLOR} size={'20px'} />
            Enter/Exit Normal Geo Fence
          </div>
          <div className={styles.replayTimeRangeHeaderText}>
            <MarkerBadge glyph={LuTriangleAlert} color={HIGH_RISK_MARKER_COLOR} size={'20px'} />
            Enter/Exit High Risk Geo Fence
          </div>
        </div>

        <div className={styles.speedLegend}>
          {SPEED_LEGEND.map(({ color, label }) => (
            <div className={styles.replayTimeRangeHeaderText} key={label}>
              <div
                className={styles.replayTimeRangeHeaderIcon}
                style={{ backgroundColor: color }}
              />
              {label}
            </div>
          ))}
        </div>

        <div>
          <label className={styles.replayTimeRangeInputLabel}>
            <input
              className={styles.replayTimeRangeInput}
              type="range"
              min={1}
              max={15}
              onChange={(e) => setStepValue(Number(e.target.value))}
              value={stepValue}
              list="stepSize"
              data-testid="replay-timeline-step-size"
            />
            <datalist id="stepSize">
              <option value="1" label="1m" />
              <option value="5" label="5m" />
              <option value="10" label="10m" />
              <option value="15" label="15m" />
            </datalist>
            <span>{stepSize}m</span>
          </label>
        </div>
      </div>
      <ReplayTimeRange
        markerTracks={markerTracks}
        rangeStartTime={rangeStartTime}
        rangeEndTime={rangeEndTime}
        selectionStartTime={selectionStartTime}
        selectionEndTime={selectionEndTime}
        setSelectionStartTime={setSelectionStartTime}
        setSelectionEndTime={setSelectionEndTime}
      />
    </div>
  )
}
